/**
 * Dialog de configuração do node Gerar Contrato
 * Permite selecionar contrato publicado, mapear variáveis e configurar signatários
 * 
 * RELEASE: 1.1.0 - 2026-09-07 - Resultado migrado para o modal de destino.
 * RELEASE: 1.0.0 - 2026-03-22 - Novo dialog de configuração
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
// InputWithVariables removido — agora usa VariablePickerCombobox para todos os campos de variável
import { VariablePickerCombobox } from '../VariablePickerCombobox';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  FileSignature,
  FileText,
  Variable,
  Users,
  Plus,
  Trash2,
  ArrowRight,
  AlertCircle,
  Send,
  Save,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { FlowNode, ContractGenerateConfig, ContractGenerateSigner } from '@/types/flows';
import { parseContractVariables } from '@/lib/contractVariableParser';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger do módulo
const logger = createLogger('ConfigureContractGenerate');

interface ConfigureContractGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: Record<string, unknown>) => void;
}

/**
 * Dialog de configuração do node de geração de contrato
 */
export function ConfigureContractGenerateDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureContractGenerateDialogProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Estado local do formulário
  const existingConfig = node.data.config as ContractGenerateConfig | undefined;

  const [publishedContractId, setPublishedContractId] = useState(existingConfig?.published_contract_id || '');
  const [publishedContractName, setPublishedContractName] = useState(existingConfig?.published_contract_name || '');
  const [variableMapping, setVariableMapping] = useState<Record<string, string>>(existingConfig?.variable_mapping || {});
  const [signers, setSigners] = useState<ContractGenerateSigner[]>(
    existingConfig?.signers || [{ name_variable: '', role: 'contratante', delivery_method: 'whatsapp' }]
  );
  const [dealSelection, setDealSelection] = useState<'from_trigger' | 'from_variable' | 'none'>(existingConfig?.deal_selection || 'from_trigger');
  const [dealVariable, setDealVariable] = useState(existingConfig?.deal_variable || '');
  const [dealVariableId, setDealVariableId] = useState(existingConfig?.deal_variable_id || '');
  const [nickname, setNickname] = useState(existingConfig?.nickname || '');
  const [autoSendSignature, setAutoSendSignature] = useState(existingConfig?.auto_send_signature ?? false);
  // Variável de resultado da execução (v1.1.0)
  const [resultVariableId, setResultVariableId] = useState(existingConfig?.result_variable_id || '');
  const [resultVariableName, setResultVariableName] = useState(existingConfig?.result_variable || '');

  // =========================================================================
  // Query: Buscar contratos publicados em produção
  // =========================================================================
  const { data: publishedContracts = [], isLoading: loadingContracts } = useQuery({
    queryKey: ['published-contracts-production', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from('published_contracts')
        .select(`
          id, name, description, status, default_signers,
          contract_templates (
            id, name, content_html
          ),
          contract_template_versions (
            id, version_number, content_html
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'production')
        .order('name');

      if (error) {
        logger.error('Erro ao buscar contratos publicados', { error });
        return [];
      }

      return data || [];
    },
    enabled: open && !!tenantId,
    staleTime: 0, // Sempre buscar dados frescos ao abrir o dialog
  });

  // =========================================================================
  // Variáveis do template do contrato selecionado
  // =========================================================================
  const templateVariables = useMemo(() => {
    if (!publishedContractId) return [];

    const selected = publishedContracts.find((pc: any) => pc.id === publishedContractId);
    if (!selected) return [];

    // Prioriza content_html da versão, senão usa o do template
    const html = (selected as any).contract_template_versions?.content_html
      || (selected as any).contract_templates?.content_html
      || '';

    if (!html) return [];

    return parseContractVariables(html);
  }, [publishedContractId, publishedContracts]);

  // =========================================================================
  // Resetar formulário quando muda o node selecionado
  // =========================================================================
  useEffect(() => {
    if (open) {
      const cfg = node.data.config as ContractGenerateConfig | undefined;
      setPublishedContractId(cfg?.published_contract_id || '');
      setPublishedContractName(cfg?.published_contract_name || '');
      setVariableMapping(cfg?.variable_mapping || {});
      setSigners(cfg?.signers || [{ name_variable: '', role: 'contratante', delivery_method: 'whatsapp' }]);
      setDealSelection(cfg?.deal_selection || 'from_trigger');
      setDealVariable(cfg?.deal_variable || '');
      setDealVariableId(cfg?.deal_variable_id || '');
      setNickname(cfg?.nickname || '');
      setAutoSendSignature(cfg?.auto_send_signature ?? false);
      setResultVariableId(cfg?.result_variable_id || '');
      setResultVariableName(cfg?.result_variable || '');
    }
  }, [open, node.id]);

  // =========================================================================
  // Handlers
  // =========================================================================

  /** Quando seleciona um contrato publicado */
  const handleContractSelect = (contractId: string) => {
    const selected = publishedContracts.find((pc: any) => pc.id === contractId);
    setPublishedContractId(contractId);
    setPublishedContractName((selected as any)?.name || '');
    // Limpa mapeamento anterior
    setVariableMapping({});

    // Pré-popula signatários com default_signers do contrato publicado
    const defaultSigners = (selected as any)?.default_signers as any[] || [];
    if (defaultSigners.length > 0) {
      setSigners(defaultSigners.map((ds: any) => ({
        name_variable: '',
        role: ds.signer_role || 'contratante',
        email_variable: '',
        phone_variable: '',
        delivery_method: ds.delivery_method || 'whatsapp',
        default_name: ds.default_name || '',
        default_email: ds.default_email || '',
        default_phone: ds.default_phone || '',
        from_template: true,
      })));
      logger.info('Signatários pré-populados do contrato publicado', { count: defaultSigners.length });
    } else {
      setSigners([{ name_variable: '', role: 'contratante', delivery_method: 'whatsapp' }]);
    }
  };

  /** Atualiza mapeamento de uma variável do template */
  const handleMappingChange = (templateKey: string, flowVariable: string) => {
    setVariableMapping(prev => ({
      ...prev,
      [templateKey]: flowVariable,
    }));
  };

  /** Adiciona novo signatário */
  const handleAddSigner = () => {
    setSigners(prev => [
      ...prev,
      { name_variable: '', role: 'contratante', delivery_method: 'whatsapp' },
    ]);
  };

  /** Remove signatário */
  const handleRemoveSigner = (index: number) => {
    setSigners(prev => prev.filter((_, i) => i !== index));
  };

  /** Atualiza campo de um signatário */
  const handleSignerChange = (index: number, field: keyof ContractGenerateSigner, value: string) => {
    setSigners(prev => prev.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    ));
  };

  /** Salva configuração */
  const handleSave = () => {
    if (!publishedContractId) {
      toast.error('Selecione um contrato publicado');
      return;
    }

    // Valida que cada signatário tem nome (via variável ou valor fixo pré-configurado)
    const hasInvalidSigner = signers.length === 0 || signers.some(s => !s.name_variable && !s.default_name);
    if (hasInvalidSigner) {
      toast.error('Configure o nome (variável ou fixo) de cada signatário');
      return;
    }

    const config: ContractGenerateConfig = {
      published_contract_id: publishedContractId,
      published_contract_name: publishedContractName,
      variable_mapping: variableMapping,
      signers,
      deal_selection: dealSelection,
      deal_variable: dealSelection === 'from_variable' ? dealVariable : undefined,
      deal_variable_id: dealSelection === 'from_variable' ? dealVariableId : undefined,
      nickname: nickname || undefined,
      auto_send_signature: autoSendSignature,
      result_variable: resultVariableName || undefined,
      result_variable_id: resultVariableId || undefined,
    };

    onSave(node.id, config as unknown as Record<string, unknown>);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-teal-500">
              <FileSignature className="h-4 w-4 text-white" />
            </div>
            Configurar Geração de Contrato
          </DialogTitle>
          <DialogDescription>
            Selecione o contrato publicado, mapeie as variáveis do fluxo e configure os signatários
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6 py-4">
            {/* ============================================ */}
            {/* SEÇÃO 1: Apelido do node */}
            {/* ============================================ */}
            <div className="space-y-2">
              <Label>Apelido do node (opcional)</Label>
              <Input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Ex: Gerar Contrato Mentoria"
              />
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 2: Seleção do contrato publicado */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-teal-500" />
                <Label className="text-base font-semibold">Contrato Publicado</Label>
              </div>

              <Select value={publishedContractId} onValueChange={handleContractSelect}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingContracts ? 'Carregando...' : 'Selecione o contrato'} />
                </SelectTrigger>
                <SelectContent>
                  {publishedContracts.map((pc: any) => (
                    <SelectItem key={pc.id} value={pc.id}>
                      <div className="flex items-center gap-2">
                        <span>{pc.name}</span>
                        {pc.description && (
                          <span className="text-xs text-muted-foreground">— {pc.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {publishedContracts.length === 0 && !loadingContracts && (
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-4 w-4" />
                  <span>Nenhum contrato em produção encontrado. Publique um contrato primeiro.</span>
                </div>
              )}
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 3: Mapeamento de variáveis */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Variable className="h-4 w-4 text-teal-500" />
                <Label className="text-base font-semibold">Mapeamento de Variáveis</Label>
              </div>

              {!publishedContractId && (
                <p className="text-sm text-muted-foreground italic">
                  Selecione um contrato para ver as variáveis disponíveis
                </p>
              )}

              {templateVariables.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Associe cada variável do contrato a uma variável do fluxo (ex: input_nome, webhook_cpf)
                  </p>
                  {templateVariables.map((tv) => (
                    <div key={tv.key} className="flex items-center gap-2">
                      <div className="flex items-center gap-1 min-w-[140px]">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {`{{${tv.key}}}`}
                        </code>
                        {tv.required && (
                          <Badge variant="destructive" className="text-[9px] px-1 py-0">*</Badge>
                        )}
                      </div>
                      <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <VariablePickerCombobox
                        tenantId={tenantId || ''}
                        value={variableMapping[tv.key] || ''}
                        onChange={(variable) => handleMappingChange(tv.key, variable?.name || '')}
                        placeholder={`Selecione a variável...`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {publishedContractId && templateVariables.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  Este contrato não possui variáveis para mapear
                </p>
              )}
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 4: Signatários */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-teal-500" />
                <Label className="text-base font-semibold">Signatários</Label>
              </div>

              <p className="text-xs text-muted-foreground">
                Signatários configurados no modelo de contrato. Para alterá-los, edite as configurações do contrato.
              </p>

              {signers.map((signer, index) => (
                <div key={index} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Signatário {index + 1}</span>
                    {/* Indica se tem dados pré-configurados do contrato publicado */}
                    {(signer.default_name || signer.default_email || signer.default_phone) && (
                      <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                        Pré-configurado
                      </Badge>
                    )}
                  </div>

                  {/* Dados fixos pré-configurados do contrato publicado */}
                  {(signer.default_name || signer.default_email || signer.default_phone) && (
                    <div className="bg-muted/50 rounded p-2 text-xs text-muted-foreground space-y-0.5">
                      <p className="font-medium text-foreground/70">Dados fixos do contrato:</p>
                      {signer.default_name && <p>Nome: <span className="font-medium">{signer.default_name}</span></p>}
                      {signer.default_email && <p>Email: <span className="font-medium">{signer.default_email}</span></p>}
                      {signer.default_phone && <p>Telefone: <span className="font-medium">{signer.default_phone}</span></p>}
                      <p className="text-[10px] mt-1 italic">Se informar variável abaixo, ela substituirá o valor fixo</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    {/* Nome — fixo (disabled) ou variável (InputWithVariables) */}
                    <div className="space-y-1">
                      <Label className="text-xs">
                        Nome {signer.default_name ? '(fixo)' : `(variável) *`}
                      </Label>
                      {signer.default_name ? (
                        <Input
                          value={signer.default_name}
                          disabled
                          className="bg-muted/50"
                        />
                      ) : (
                        <VariablePickerCombobox
                          tenantId={tenantId || ''}
                          placeholder="Selecione a variável do nome..."
                          value={signer.name_variable}
                          onChange={(variable) => handleSignerChange(index, 'name_variable', variable?.name || '')}
                        />
                      )}
                    </div>

                    {/* Papel — Select (desabilitado se pré-configurado) */}
                    <div className="space-y-1">
                      <Label className="text-xs">Papel</Label>
                      <Select
                        value={signer.role}
                        onValueChange={(v) => handleSignerChange(index, 'role', v)}
                        disabled={!!signer.from_template}
                      >
                        <SelectTrigger className="h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contratante">Contratante</SelectItem>
                          <SelectItem value="contratado">Contratado</SelectItem>
                          <SelectItem value="testemunha">Testemunha</SelectItem>
                          <SelectItem value="fiador">Fiador</SelectItem>
                          <SelectItem value="responsavel">Responsável</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Email — fixo (disabled) ou variável (InputWithVariables) */}
                    <div className="space-y-1">
                      <Label className="text-xs">Email {signer.default_email ? '(fixo)' : '(variável)'}</Label>
                      {signer.default_email ? (
                        <Input
                          value={signer.default_email}
                          disabled
                          className="bg-muted/50"
                        />
                      ) : (
                        <VariablePickerCombobox
                          tenantId={tenantId || ''}
                          placeholder="Selecione a variável do email..."
                          value={signer.email_variable || ''}
                          onChange={(variable) => handleSignerChange(index, 'email_variable', variable?.name || '')}
                        />
                      )}
                    </div>

                    {/* Telefone — fixo (disabled) ou variável (InputWithVariables) */}
                    <div className="space-y-1">
                      <Label className="text-xs">Telefone {signer.default_phone ? '(fixo)' : '(variável)'}</Label>
                      {signer.default_phone ? (
                        <Input
                          value={signer.default_phone}
                          disabled
                          className="bg-muted/50"
                        />
                      ) : (
                        <VariablePickerCombobox
                          tenantId={tenantId || ''}
                          placeholder="Selecione a variável do telefone..."
                          value={signer.phone_variable || ''}
                          onChange={(variable) => handleSignerChange(index, 'phone_variable', variable?.name || '')}
                        />
                      )}
                    </div>
                  </div>

                  {/* Método de entrega — desabilitado se pré-configurado */}
                  <div className="space-y-1">
                    <Label className="text-xs">Método de entrega</Label>
                    <Select
                      value={signer.delivery_method}
                      onValueChange={(v) => handleSignerChange(index, 'delivery_method', v)}
                      disabled={!!signer.from_template}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 5: Seleção do Deal */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ArrowRight className="h-4 w-4 text-teal-500" />
                <Label className="text-base font-semibold">Origem do Negócio (Deal)</Label>
              </div>

              <Select value={dealSelection} onValueChange={(v: 'from_trigger' | 'from_variable' | 'none') => setDealSelection(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="from_trigger">Do contexto do gatilho (deal_id automático)</SelectItem>
                  <SelectItem value="from_variable">De uma variável do fluxo</SelectItem>
                  <SelectItem value="none">Sem negócio</SelectItem>
                </SelectContent>
              </Select>

              {dealSelection === 'from_variable' && (
                <VariablePickerCombobox
                  tenantId={tenantId || ''}
                  value={dealVariableId || dealVariable}
                  onChange={(variable) => {
                    setDealVariableId(variable?.id || '');
                    setDealVariable(variable?.name || '');
                  }}
                  placeholder="Selecione a variável com deal_id..."
                />
              )}
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 6: Envio automático para assinatura */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-teal-500" />
                  <Label className="text-base font-semibold">Enviar para assinatura</Label>
                </div>
                <Switch
                  checked={autoSendSignature}
                  onCheckedChange={setAutoSendSignature}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Quando ativo, após gerar o PDF o contrato será enviado automaticamente para assinatura dos signatários configurados.
              </p>
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 7: Salvar resultado da execução */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Save className="h-4 w-4 text-teal-500" />
                <Label className="text-base font-semibold">Salvar resultado em variável</Label>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                Armazena o resultado da execução para usar em nodes de condição
              </p>
              <VariableDestinationPicker
                tenantId={tenantId || ''}
                value={resultVariableId || resultVariableName}
                valueName={resultVariableName}
                onChange={(variable) => {
                  if (variable) {
                    setResultVariableId(variable.id);
                    setResultVariableName(variable.name);
                  } else {
                    setResultVariableId('');
                    setResultVariableName('');
                  }
                }}
                allowClear
                placeholder="Selecione ou crie uma variável"
              />
              <div className="bg-muted/50 rounded p-2 space-y-1">
                <p className="text-xs text-muted-foreground font-medium">Valores possíveis:</p>
                <div className="flex flex-wrap gap-2">
                  <code className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded text-[10px]">
                    generated
                  </code>
                  <span className="text-xs text-muted-foreground">Contrato criado e PDF gerado</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <code className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded text-[10px]">
                    reused
                  </code>
                  <span className="text-xs text-muted-foreground">Draft existente reutilizado</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <code className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-[10px]">
                    sent_signature
                  </code>
                  <span className="text-xs text-muted-foreground">Enviado para assinatura</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <code className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded text-[10px]">
                    error
                  </code>
                  <span className="text-xs text-muted-foreground">Erro durante a geração</span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-teal-500 hover:bg-teal-600">
            <FileSignature className="h-4 w-4 mr-2" />
            Salvar Configuração
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
